import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { CurrentUser } from "../../common/providers/current-user";
import { ApiBaseService } from "../../common/providers/api-base-service";
import { Test } from "../../common/models/test";
import { Organization } from "../../common/models/organization";
import { Athlete } from "../../common/models/athlete";
import { map } from "rxjs/operators";
import { ReactivityTestMeasure } from "../../common/models/reactivity-test-measure";
import { User } from "../../common/models/user";
import { mapTestItemRightsToRowRights, TestItemRightsList } from "./test-rights";

export const PVC_TEST_ID = 100;

@Injectable({providedIn: 'root'})
export class TestProvider {
  constructor(
    private currentUser: CurrentUser,
    private api: ApiBaseService
  ) {}

  /**
   * Find all Tests
   */
  // public findAll(): Observable<Test[]> {
  //     this.api.call('get', 'tests/', {}, this.currentUser.getAccessToken(), true).pipe(
  //      map((data: Test[]) => Test.fromApiList(data)),
  //     );
  // }

  public get(id: number, queryParams = {}): Observable<Test> {
    return this.api.call('get', `tests/${id}`, queryParams, this.currentUser.getAccessToken(), true).pipe(
      map((data: any) => {
        return Test.fromApi(data)
      }),
    )
  }

  /**
   * Find a test by his type
   */
  public findAllWithMaxDate(organization: Organization, athleteUser: User, langId: number, order: 'name_asc' | 'date_desc' = 'name_asc' ): Observable<TestShortResume[]> {
    const params = {
      lang: langId,
      sort: order
    };

    const url = `organizations/${organization.code}/athletes/${athleteUser.id}/tests`;
    return this.api.call('get', url, params, this.currentUser.getAccessToken(), true).pipe(
      map((result: any) => {
        const map: TestShortResume[] = [];
        for (const data of result) {
          map.push(
            new TestShortResume({
              id: data.id,
              name: data.name,
              date: data.date,
              read_only: data.read_only,
              comment: data.comment,
              creater_id: data.creater_id,
              values: data.values,
              rights: mapTestItemRightsToRowRights(data.rights),
            })
          );
        }
        return map;
      }),
    );
  }

  public findAllUniqueWithMaxDate(organization: Organization, athleteUser: User, langId: number, order: 'name_asc' | 'date_desc' = 'name_asc'): Observable<TestShortResume[]> {
    return this.findAllWithMaxDate(organization, athleteUser, langId, order).pipe(
      map((tests: TestShortResume[]) => {
        const uniqueTests: TestShortResume[] = [];
        for (const test of tests) {
          if (!uniqueTests.some(t => t.id === test.id)) {
            uniqueTests.push(test);
          }
        }
        return uniqueTests;
      }),
    );
  }

  public getMeasures(organization: Organization, athlete: Athlete, test: Test, queryParams = {}): Observable<any> {
    const url = `organizations/${organization.code}/athletes/${athlete.userId}/tests/${test.id}`;
    return this.api.call('get', url, queryParams, this.currentUser.getAccessToken(), true);
  }

  public getAllMeasures(organization: Organization, athlete: Athlete, queryParams = {}): Observable<any> {
    return this.api.call('get', `organizations/${organization.code}/athletes/${athlete.userId}/tests/measures`, queryParams, this.currentUser.getAccessToken(), true);
  }

  public getMetrics(organization: Organization, athleteUser: User, testId: number, queryParams: { lang?: number, grouped?: boolean } = {}): Observable<any> {
    return this.api.call('get', `organizations/${organization.code}/athletes/${athleteUser.id}/tests/${testId}/metrics/`, queryParams, this.currentUser.getAccessToken(), true);
  }

  public getUnits(): Observable<any> {
    return this.api.call('get', 'tests/units/', {}, this.currentUser.getAccessToken(), true);
  }

  public saveTest(organization: Organization, athleteUser: User, test: Test): Observable<any> {
    return this.api.call('post', `organizations/${organization.code}/athletes/${athleteUser.id}/tests/`, test, this.currentUser.getAccessToken(), true);
  }

  public deleteTest(organization: Organization, athleteUser: User, test: Test): Observable<any> {
    return this.api.call('delete', `organizations/${organization.code}/athletes/${athleteUser.id}/tests/${test.id}`, {}, this.currentUser.getAccessToken(), true);
  }

  public saveMeasure(organization: Organization, athleteUser: User, test: Test, measure: any): Observable<any> {
    return this.api.call('post', `organizations/${organization.code}/athletes/${athleteUser.id}/tests/${test.id}/measures/`, measure, this.currentUser.getAccessToken(), true);
  }

  public deleteMeasure(organization: Organization, athleteUser: User, test: Test, measureId: number): Observable<any> {
    return this.api.call('delete', `organizations/${organization.code}/athletes/${athleteUser.id}/tests/${test.id}/measures/${measureId}`, {}, this.currentUser.getAccessToken(), true);
  }

  public saveReactivityMeasure(organization: Organization, athlete: Athlete, test: Test, payload: any): Promise<ReactivityTestMeasure> {
    return new Promise<any>((resolve, reject) => {

      const url = `organizations/${organization.code}/athletes/${athlete.userId}/tests/${test.type}`;

      this.api.call('post', url, payload, this.currentUser.getAccessToken(), true).pipe(
        map((data: any) => {
          const athlete = Athlete.fromAPI(data.athlete);
        }),
      );
    });
  }

  public savePowerMeasure(organization: Organization, athlete: Athlete, test: Test, payload: any): Promise<ReactivityTestMeasure> {
    return new Promise<any>((resolve, reject) => {

      const url = `organizations/${organization.code}/athletes/${athlete.userId}/tests/${test.type}`;
      this.api.call('post', url, payload, this.currentUser.getAccessToken(), true).pipe(
        map((data: any) => {
          const athlete = Athlete.fromAPI(data.athlete);
        }),
      );

      // this._api.call('post', url, payload, this.currentUser.getAccessToken(), true).then((data: any) => {
      //
      //     const athlete = Athlete.fromAPI(data.athlete);
      //     this.storage.save([athlete])
      //         .then(() => {
      //             resolve(PowerTestMeasure.fromAPI(data.measure));
      //         });
      // }).catch(
      //     (error) => reject(error)
      // );
    });
  }

  public saveBilateralMeasure(organization: Organization, athlete: Athlete, test: Test, payload:any ): Promise<ReactivityTestMeasure> {
    return new Promise<any>((resolve, reject) => {
      const url = `organizations/${organization.code}/athletes/${athlete.userId}/tests/${test.type}`;
      this.api.call('post', url, payload, this.currentUser.getAccessToken(), true).pipe(
        map((data: any) => {
          const athlete = Athlete.fromAPI(data.athlete);
        }),
      );
    });
  }

  public saveStrengthMeasure(organization: Organization, athlete: Athlete, test: Test, payload:any): Promise<ReactivityTestMeasure> {
    return new Promise<any>((resolve, reject) => {

      const url = `organizations/${organization.code}/athletes/${athlete.userId}/tests/${test.type}`;
      this.api.call('post', url, payload, this.currentUser.getAccessToken(), true).pipe(
        map((data: any) => {
          const athlete = Athlete.fromAPI(data.athlete);
        }),
      );
    });
  }


  public findLatestTest({organization, athleteUser, langId}:{organization: Organization, athleteUser: User, langId: number}): Observable<TestShortResume> {
    return this.findAllWithMaxDate(organization, athleteUser, langId, 'date_desc').pipe(
      map((tests: TestShortResume[]) => {
        return tests[0];
      }),
    );
  }

}

export class TestLeftMenu {
  public id;
  public name;
  public date;
  public read_only;
  public rights;

  constructor(id: any, name: any, date: any, read_only: any, rights: any) {
    this.id = id;
    this.name = name;
    this.date = date;
    this.read_only = read_only;
    this.rights = rights;
  }
}

export class TestShortResume {
  public id: number;
  public name: string;
  public date: string;
  public read_only: boolean;
  public comment: string;
  public creater_id: number;
  public values: TestValues[];
  public rights: TestItemRightsList;

  constructor({id, name, date, read_only, comment, creater_id, values, rights}: {id: number, name: string, date: string, read_only: boolean, comment: string, creater_id: number, values: any, rights: TestItemRightsList}) {
    this.id = id;
    this.name = name;
    this.date = date;
    this.read_only = read_only;
    this.comment = comment;
    this.creater_id = creater_id;
    this.values = values;
    this.rights = rights;
  }
}

export class TestValues {
  public id: number;
  public test_measure_id: number;
  public test_metric_id: number;
  public value: number;
  public metric: Metrics;

  constructor({id, test_measure_id, test_metric_id, value, metric}: {id: number, test_measure_id: number, test_metric_id: number, value: number, metric: Metrics}) {
    this.id = id;
    this.test_measure_id = test_measure_id;
    this.test_metric_id = test_metric_id;
    this.value = value;
    this.metric = metric;
  }
}

export class Metrics {
  public id: number;
  public name: string;
  public test_unit_id: number;
  public test_unit_name: string;

  constructor({id, name, test_unit_id, test_unit_name}: {id: number, name: string, test_unit_id: number, test_unit_name: string}) {
    this.id = id;
    this.name = name;
    this.test_unit_id = test_unit_id;
    this.test_unit_name = test_unit_name;
  }
}
